(ns leg.route
  "The address bar as the description of what is being looked at.

  A reader is a place you go into and come back out of: a feed, then an
  article inside it, then back to the feed, then back to where you started.
  That is what the browser's own back button is for, and it only works if
  every one of those is an address. So the address is written when the
  window moves, and read when the browser moves it back.

  Eight shapes, and anything else is the start page rather than an error -
  an address somebody mistyped or a link that outlived its article should
  open the reader, not a message about a route.

      /                           every feed
      /feed/<feed-id>             one feed
      /feed/<feed-id>/<hash>      an article in it
      /group/<name>               every feed of one group
      /group/<name>/<id>/<hash>   an article in a group
      /read/<feed-id>/<hash>      an article with no list behind it
      /settings                   the account, the keys, the feeds
      /admin                      the readers, for whoever runs this

  and `?pane=feeds` on any of the first six, which is the one narrow
  window standing in front of its list of feeds.

  The last two are `:page`: not a view of a list but somewhere else, over
  the whole window, one at a time. They are addresses because they are
  places - you go into one and come back out of it, and that is what the
  back button is for. The admin page is drawn on the strength of a flag
  from the server and every call it makes is checked again there, so the
  address is a way to the page and not a way past anything.

  An article's id is `<feed-id>/<hash>` (see `leser.ids`), so the slash in
  it is a real path separator and nothing in those two shapes has to be
  escaped: a feed id is a slug and fourteen base-36 digits, a hash is
  fourteen more.

  A group is the one thing in an address that somebody typed, so it is the
  one thing that is escaped. It has no id and there is no file of groups: a
  group is a word written on a subscription, it exists for as long as a
  feed carries it, and the name *is* the group. So the address holds the
  name, percent-encoded - `/group/Nachrichten%20DE` - and a name with a
  slash in it survives because `pathname` hands `%2F` back as it was.

  The three article shapes are one distinction: which list is behind it.
  Read from inside a feed, going back means that feed; read from inside a
  group, that group; read out of everything at once or out of the starred,
  going back means everything at once. Folding them together would make two
  of the three wrong.

  The filter is a query parameter and not a path segment, because it is not
  a place: pressing All where Unread was does not put anything on the stack
  for the back button to find. `unread` is the default and is left out.

  There are two of them, one per list, because they are two questions:
  `filter` is which articles of a feed are shown and `feed-filter` is which
  feeds are. Both default to unread and both are left out at that value, so
  the shortest address is the one a reader wants on opening the window -
  what is new, and nothing else.

  The pane is a parameter for a different reason. A wide window shows all
  three at once, so which one a narrow window is standing in is not another
  thing being looked at - it is the same feed and the same article seen
  through a screen that has room for one of them. So it hangs off the
  address rather than being one: `/feed/x?pane=feeds` is that feed, with
  the phone one step to the left of it. Coming back out of the feed list is
  still a move, and that is why it is in the address at all - a reload while
  it is open used to land somewhere else, and the back button walked out of
  the reader instead of out of the list.

  Two of the three panes are never written. The article's pane is what
  naming an article already says, and the list of articles is what is left -
  the same economy as `unread`.

  Nothing here touches `js/history` or the state - this only turns one into
  the other, which is what makes it testable."
  (:require [clojure.string :as str]))

(def filters
  "Every filter that may appear in an address, by the name it appears under."
  {"unread" :unread "all" :all "starred" :starred})

(def default-filter :unread)

(def feed-filters
  "Every filter the list of feeds may be under, by the name it appears
  under. Two and not three, because a feed is not starred: the names of the
  papers are either all of them or the ones with something left in them."
  {"unread" :unread "all" :all})

(def default-feed-filter
  "Only the feeds with something unread in them. The list of feeds is read
  to find out where to go next, and forty names of which two have anything
  new is forty names to look past."
  :unread)

(def default-pane
  "The pane a narrow window is in when the address says nothing else: the
  list of articles, which is the middle of the three."
  :articles)

(defn- segments [pathname]
  (->> (str/split (or pathname "") #"/")
       (remove str/blank?)
       vec))

(defn- article-of [feed-id hash]
  (when (and (seq feed-id) (seq hash))
    (str feed-id "/" hash)))

(defn- decoded
  "One path segment as what somebody typed. A name that was escaped badly
  is taken as it stands rather than thrown: this file turns an address into
  a thing to look at, and an address nobody can read is the start page."
  [s]
  (when (seq s)
    (or (try (js/decodeURIComponent s) (catch :default _ nil)) s)))

(defn parse
  "One address as what the window should be showing. Always a map with all
  seven keys, so a caller never has to tell \"not in the address\" from
  \"not parsed\".

  The pane is worked out here rather than by whoever asks: an address that
  names an article is the article's pane, and one that does not is the list
  of articles unless it says otherwise. Three answers out of one place, so
  there is no second opinion about it anywhere else."
  [href]
  (let [url    (try (js/URL. href) (catch :default _ nil))
        param  (fn [k] (some-> url .-searchParams (.get k)))
        segs   (segments (some-> url .-pathname))
        filter (or (get filters (param "filter")) default-filter)
        ffilter (or (get feed-filters (param "feed-filter")) default-feed-filter)
        feeds? (= "feeds" (param "pane"))
        [a b c] segs
        base   (case (count segs)
                 0 {}
                 1 (case a
                     "admin"    {:page :admin}
                     "settings" {:page :settings}
                     nil)
                 2 (case a
                     "feed"  {:feed b}
                     "group" (when-let [g (decoded b)] {:group g})
                     nil)
                 3 (case a
                     "feed" {:feed b :article (article-of b c)}
                     "read" {:article (article-of b c)}
                     nil)
                 4 (when (= "group" a)
                     (when-let [g (decoded b)]
                       {:group g :article (article-of c (nth segs 3))}))
                 nil)
        m      (merge {:feed nil :group nil :article nil :page nil
                       :filter filter :feed-filter ffilter}
                      base)]
    (assoc m :pane (cond (:article m) :article
                         feeds?       :feeds
                         :else        default-pane))))

(defn href
  "The address for what the window is showing. Relative on purpose: it is
  handed to `history.pushState`, which resolves it, and a reader that wrote
  its own host into every address would be one redirect away from writing
  the wrong one."
  [{:keys [feed group article page filter feed-filter pane]}]
  (let [filter   (or filter default-filter)
        ffilter  (or feed-filter default-feed-filter)
        slash    (when article (str/index-of article "/"))
        in-feed? (and feed slash (str/starts-with? article (str feed "/")))
        in-group? (and group article (not in-feed?))
        hash     (when slash (subs article (inc slash)))
        ;; An article names its own pane, so a map that says both is not an
        ;; argument to settle here: the article was asked for last.
        feeds?   (and (= :feeds pane) (nil? article))
        params   (cond-> []
                   (not= default-filter filter)
                   (conj (str "filter=" (name filter)))
                   (not= default-feed-filter ffilter)
                   (conj (str "feed-filter=" (name ffilter)))
                   feeds? (conj "pane=feeds"))]
    (str (cond
           ;; A page and not a view of a list: it carries no filter, and the
           ;; feed the window was on is behind it rather than in it.
           page      (str "/" (name page))
           in-feed?  (str "/feed/" feed "/" hash)
           in-group? (str "/group/" (js/encodeURIComponent group) "/" article)
           article   (str "/read/" article)
           feed      (str "/feed/" feed)
           group     (str "/group/" (js/encodeURIComponent group))
           :else     "/")
         (when-not (or page (empty? params))
           (str "?" (str/join "&" params))))))
